import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  TextField,
  Chip,
  CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CropIcon from '@mui/icons-material/Crop';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { v4 as uuidv4 } from 'uuid';
import { sheetsService } from '../services/googleSheetsService';

// Master Video List spreadsheet ID
const MASTER_VIDEO_LIST_SPREADSHEET_ID = '1aVv8xC1HIB1XQDDwF8wiLtkB9c-D_g4zPnhFhbizkTo';

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface VideoMetadata {
  width: number;
  height: number;
  duration: number;
  aspectRatio: number;
}

export const UploadVideo = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Player dimensions state
  const [playerRect, setPlayerRect] = useState<{ width: number; height: number } | null>(null);

  // Upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Video states
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoMetadata, setVideoMetadata] = useState<VideoMetadata | null>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState<boolean>(false);

  // Crop states
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 0, height: 0 });
  const [dragCorner, setDragCorner] = useState<'tl' | 'tr' | 'bl' | 'br' | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; cropArea: CropArea } | null>(null);
  const [isAIDetecting, setIsAIDetecting] = useState(false);
  const [showCropOverlay, setShowCropOverlay] = useState<boolean>(false);

  // Processing states
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [processingStep, setProcessingStep] = useState<string>('');

  // Add state for videoId
  const [videoId, setVideoId] = useState<string | null>(null);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Add state for 4:3 video detection and blurred preview
  const [is43Video, setIs43Video] = useState<boolean>(false);
  const [blurredPreviewUrl, setBlurredPreviewUrl] = useState<string | null>(null);
  const [isPillarboxed43, setIsPillarboxed43] = useState<boolean>(false);
  const [isLetterboxed, setIsLetterboxed] = useState<boolean>(false);
  const [isPillarboxed, setIsPillarboxed] = useState<boolean>(false);
  const [isWideFile, setIsWideFile] = useState<boolean>(false);

  // Add state for tracking video results
  const [processedVideoUrl, setProcessedVideoUrl] = useState<string | null>(null);
  const [verticalVideoUrl, setVerticalVideoUrl] = useState<string | null>(null);
  const [showRetryVertical, setShowRetryVertical] = useState<boolean>(false);

  // Add state for YouTube download functionality
  const [youtubeUrl, setYoutubeUrl] = useState<string>('');
  const [isDownloadingFromYoutube, setIsDownloadingFromYoutube] = useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [downloadStep, setDownloadStep] = useState<string>('');

  // States for YouTube downloaded video path and source tracking
  const [youtubeServerFilePath, setYoutubeServerFilePath] = useState<string | null>(null);
  const [youtubeOriginalFileName, setYoutubeOriginalFileName] = useState<string | null>(null);
  const [isVideoFromYoutubeSource, setIsVideoFromYoutubeSource] = useState<boolean>(false);

  // Effect to handle youtubeUrl query parameter
  const location = useLocation();

  const [isDragging, setIsDragging] = useState(false);
  const [videoManagerRowIndex, setVideoManagerRowIndex] = useState<number | null>(null);
  const [songFromVideoManager, setSongFromVideoManager] = useState<string | null>(null);
  const [artistFromVideoManager, setArtistFromVideoManager] = useState<string | null>(null);

  const processedQueryUrlRef = useRef<string | null>(null); // Ref to track processed YouTube URL from query
  const initialYoutubeUrlRef = useRef<string | null>(null); // Ref to track the first YouTube URL loaded from query params for this instance
  const completionPollRef = useRef<NodeJS.Timeout | null>(null); // Ref for completion polling
  const processedQueryParamsRef = useRef<boolean>(false); // Ref to prevent multiple query param processing

  // Function to update Master Video List with S3 URLs
  const updateMasterVideoListUrls = useCallback(async (horizontalUrl: string, verticalUrl: string) => {
    // Extract fresh data from URL parameters instead of relying on potentially reset state
    const params = new URLSearchParams(location.search);
    const rowIndexFromUrl = params.get('videoManagerRowIndex');
    const songFromUrl = params.get('song');
    const artistFromUrl = params.get('artist');
    
    // Use fresh URL parameters instead of potentially reset state
    const finalRowIndex = rowIndexFromUrl ? parseInt(rowIndexFromUrl, 10) : (videoManagerRowIndex ?? null);
    const finalSong = songFromUrl || songFromVideoManager || null;
    const finalArtist = artistFromUrl || artistFromVideoManager || null;
    
    // Only update if we have the necessary data 
    if (finalRowIndex === null || !finalSong || !finalArtist) {
      console.log('[Sheet Update] Skipping sheet update - missing data:', { 
        finalRowIndex, 
        finalSong, 
        finalArtist 
      });
      return;
    }

    try {
      console.log(`[Sheet Update] Updating Master Video List row ${finalRowIndex + 1} for ${finalArtist} - ${finalSong}`);
      
      const headers = await sheetsService.getHeaders(MASTER_VIDEO_LIST_SPREADSHEET_ID, 'Sheet1');
      const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
      const verticalUrlColIndex = headers.findIndex(h => h.toLowerCase().includes('vertical url'));
      
      if (s3UrlColIndex === -1) {
        console.error('[Sheet Update] S3 URL column not found in headers');
        return;
      }
      
      if (verticalUrlColIndex === -1) {
        console.error('[Sheet Update] Vertical URL column not found in headers');
        return;
      }

      // Prepare batch updates for both columns
      const updates = [
        {
          range: `Sheet1!${String.fromCharCode(65 + s3UrlColIndex)}${finalRowIndex + 1}`,
          values: [[horizontalUrl]]
        },
        {
          range: `Sheet1!${String.fromCharCode(65 + verticalUrlColIndex)}${finalRowIndex + 1}`,
          values: [[verticalUrl]]
        }
      ];

      console.log('[Sheet Update] Batch updates:', updates);
      
      // Execute batch update
      await sheetsService.batchUpdateValues(MASTER_VIDEO_LIST_SPREADSHEET_ID, updates);
      
      console.log('[Sheet Update] Successfully updated Master Video List');
      enqueueSnackbar('✅ Master Video List updated with S3 URLs', { variant: 'success' });

      // Navigate back to VideoManager after successful sheet update
      setTimeout(() => {
        console.log('[Sheet Update] Navigating back to VideoManager');
        navigate('/video-manager');
      }, 2500); // Give user time to see the success message
      
    } catch (error) {
      console.error('[Sheet Update] Error updating Master Video List:', error);
      enqueueSnackbar(`⚠️ Failed to update Master Video List: ${error instanceof Error ? error.message : 'Unknown error'}`, { 
        variant: 'warning' 
      });
    }
  }, [enqueueSnackbar, navigate, videoManagerRowIndex, songFromVideoManager, artistFromVideoManager]);

  // Function to poll for completion when SSE drops
  const pollForCompletion = useCallback(async (videoId: string) => {
    console.log(`[Completion Poll] Starting polling for videoId: ${videoId}`);
    
    let pollCount = 0;
    const maxPolls = 60; // 5 minutes max
    
    const checkCompletion = async () => {
      try {
        pollCount++;
        const response = await fetch(`/api/video-completion/${videoId}`);
        const data = await response.json();
        
        console.log(`[Completion Poll] Poll ${pollCount}/${maxPolls}, response:`);
        console.log('  completed:', data.completed);
        console.log('  s3Url:', data.s3Url);
        console.log('  verticalUrl:', data.verticalUrl);
        
        if (data.completed && data.s3Url && data.verticalUrl) {
          console.log(`[Completion Poll] Found completion data!`, data);
          
          // Clear polling
          if (completionPollRef.current) {
            clearInterval(completionPollRef.current);
            completionPollRef.current = null;
          }
          
          // Update state
          setProcessedVideoUrl(data.s3Url);
          setVerticalVideoUrl(data.verticalUrl);
          setProcessingProgress(100);
          setProcessingStep('Processing complete! Both videos ready.');
          enqueueSnackbar('Video processed successfully! Both horizontal and vertical videos are ready.', { variant: 'success' });
          
          // Update Master Video List with both URLs (this will also handle navigation)
          updateMasterVideoListUrls(data.s3Url, data.verticalUrl);
          
          setIsProcessing(false);
        } else if (data.completed && data.s3Url && !data.verticalUrl) {
          // Only horizontal is done, vertical might still be processing
          setProcessingStep('🎬 Creating Vertical Output ⚡');
          setProcessingProgress(85);
        } else if (pollCount >= maxPolls) {
          console.log(`[Completion Poll] Max polls reached without completion`);
          // Clear polling
          if (completionPollRef.current) {
            clearInterval(completionPollRef.current);
            completionPollRef.current = null;
          }
          setProcessingStep('Processing timeout - please check VideoManager');
          enqueueSnackbar('Processing may have completed. Please check VideoManager.', { variant: 'warning' });
          setIsProcessing(false);
        }
      } catch (error) {
        console.error('[Completion Poll] Error checking completion:', error);
      }
    };
    
    // Start polling every 5 seconds
    completionPollRef.current = setInterval(checkCompletion, 5000);
    
    // Also check immediately
    checkCompletion();
  }, [enqueueSnackbar, updateMasterVideoListUrls]);

  const resetForm = () => {
    setSelectedFile(null);
    setVideoUrl('');
    setVideoMetadata(null);
    setIsVideoLoaded(false);
    setCropArea({ x: 0, y: 0, width: 0, height: 0 });
    setShowCropOverlay(false);
    setIsProcessing(false);
    setProcessingProgress(0);
    setProcessingStep('');
    setUploadError(null);
    setVideoId(null);
    
    // Clean up video result state
    setProcessedVideoUrl(null);
    setVerticalVideoUrl(null);
    setShowRetryVertical(false);
    
    // Clean up 4:3 video state and blurred preview
    setIs43Video(false);
    setIsPillarboxed43(false);
    setIsLetterboxed(false);
    setIsPillarboxed(false);
    setIsWideFile(false);
    if (blurredPreviewUrl) {
      URL.revokeObjectURL(blurredPreviewUrl);
      setBlurredPreviewUrl(null);
    }
    
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadFromYoutube = useCallback(async (urlToDownload?: string) => {
    const finalUrl = urlToDownload || youtubeUrl; // Use parameter or component state youtubeUrl
    if (!finalUrl) {
      enqueueSnackbar('UploadVid: YT URL parameter is missing.', { variant: 'error' });
      return;
    }

    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)[\w-]+/;
    if (!youtubeRegex.test(finalUrl.trim())) {
      enqueueSnackbar('UploadVid: YT URL parameter is invalid format.', { variant: 'error' });
      return;
    }

    const currentExecutionVideoId = uuidv4(); // Generate videoId for this specific execution
    console.log(`[DownloadYT] Initiating with videoId: ${currentExecutionVideoId}`);

    setIsDownloadingFromYoutube(true);
    setDownloadProgress(0);
    setDownloadStep('Starting YouTube download...');
    setSelectedFile(null); 
    setVideoUrl('');      
    setIsVideoFromYoutubeSource(true); 

    setVideoId(currentExecutionVideoId); // Set the main component videoId state

    // Close any previous EventSource instances
    // eventSourceRef.current is for general cleanup, direct manipulation here might be tricky
    // The main eventSource state variable should be prioritized for active connections
    if (eventSource) {
      console.log('[DownloadYT] Closing existing eventSource (state) before new download.', eventSource);
      eventSource.close();
      setEventSource(null); 
    }
    // Also ensure ref is cleared if it was somehow still holding an old one, though state should be master
    if (eventSourceRef.current) {
        console.log('[DownloadYT] Closing existing eventSourceRef.current before new download.');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
    }


    const downloadSseUrl = process.env.NODE_ENV === 'development'
              ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/video-progress/${encodeURIComponent(currentExecutionVideoId)}`
      : `/api/video-progress/${encodeURIComponent(currentExecutionVideoId)}`;
    
    const downloadSSE = new EventSource(downloadSseUrl);
    setEventSource(downloadSSE); // Store this SSE connection in state (for download phase)
    eventSourceRef.current = downloadSSE; // Also update ref for safety / consistency if other parts use it

    downloadSSE.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.phase === 'youtube_starting' || data.phase === 'youtube_polling' || data.phase === 'youtube_downloading' || data.phase === 'youtube_complete') {
          if (data.percent !== undefined) {
            setDownloadProgress(data.percent);
          }
          if (data.message) {
            setDownloadStep(data.message);
          }
        }
        if (data.phase === 'youtube_error' && data.error) {
            console.error('YouTube Download SSE Error:', data.error);
            enqueueSnackbar(`Download Error: ${data.error}`, { variant: 'error' });
            setIsDownloadingFromYoutube(false);
            setDownloadProgress(0);
            setDownloadStep('Download failed');
            downloadSSE.close();
            if (eventSourceRef.current === downloadSSE) eventSourceRef.current = null;
            setEventSource(prev => prev === downloadSSE ? null : prev);
        }
      } catch (error) {
        console.error('Error parsing YouTube download SSE data:', error);
      }
    };

    downloadSSE.onerror = (event) => {
      console.error('YouTube Download SSE connection error:', event);
      if (downloadSSE.readyState === EventSource.CLOSED) {
        setIsDownloadingFromYoutube(false);
        enqueueSnackbar('YouTube download connection failed', { variant: 'error' });
        setDownloadStep('Connection failed');
        if (eventSourceRef.current === downloadSSE) eventSourceRef.current = null;
        setEventSource(prev => prev === downloadSSE ? null : prev);
      }
    };

    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/download-youtube`
        : '/api/download-youtube';
        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: currentExecutionVideoId, // Use the locally generated videoId
          youtubeUrl: finalUrl.trim(),
          song: songFromVideoManager,
          artist: artistFromVideoManager
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Download failed: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || result.error || 'Download failed with unknown server error');
      }
      
      enqueueSnackbar('YouTube video downloaded to server. Preparing preview...', { variant: 'info' });
      setDownloadStep('Download to server complete. Preparing preview...');
      setDownloadProgress(100);
      
      setYoutubeServerFilePath(result.filePath); 
      setYoutubeOriginalFileName(result.downloadedFileName);
      
      const serverBaseUrl = process.env.NODE_ENV === 'development' ? (process.env.REACT_APP_API_URL || 'http://localhost:3001') : '';
      const fullVideoPathOnServer = `${serverBaseUrl}${result.filePath}`;
      setVideoUrl(fullVideoPathOnServer); // This will trigger onLoadedMetadata -> handleVideoLoaded
      videoRef.current?.load(); 

      // Close the download-specific SSE connection as its job is done.
      if (downloadSSE && downloadSSE.readyState !== EventSource.CLOSED) {
        downloadSSE.close();
      }
      // Clear the eventSource state if it was the downloadSSE
      setEventSource(prev => prev === downloadSSE ? null : prev);
      if (eventSourceRef.current === downloadSSE) eventSourceRef.current = null;
      
      setIsDownloadingFromYoutube(false); 

      // NOW, set up the main SSE connection for video processing progress
      // using the same currentExecutionVideoId.
      console.log('[DownloadYT] Setting up main processing SSE for videoId:', currentExecutionVideoId);
      const processingSseUrl = process.env.NODE_ENV === 'development' 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/video-progress/${encodeURIComponent(currentExecutionVideoId)}`
        : `/api/video-progress/${encodeURIComponent(currentExecutionVideoId)}`;
      
      const processingSSE = new EventSource(processingSseUrl);
      setEventSource(processingSSE); // This is the main eventSource for processing
      eventSourceRef.current = processingSSE; // Update ref

      processingSSE.onmessage = (event) => {
        console.log('[SSE Debug] Main Processing Event (from YouTube flow):', event.data);
        try {
          const data = JSON.parse(event.data);
          if (data.percent !== undefined) {
            // For YouTube flow, "upload" (30%) is skipped. Processing starts effectively from 0.
            // So, if backend sends FFmpeg progress as 0-100 (data.percent),
            // map it to the 30-100% range of the overall progress bar.
            const newProgress = Math.round(30 + (data.percent * 0.7)); 
            setProcessingProgress(newProgress);

            if (data.phase === 'vertical_generation') {
              setProcessingStep('🎬 Creating Vertical Output ⚡');
            } else if (data.phase === 'vertical_processing') {
              setProcessingStep(`🎬 Creating Vertical Output ⚡ (Job: ${data.sieveJobId || 'processing'})`);
            } else if (data.phase === 'vertical_polling') {
              setProcessingStep(`🎬 Creating Vertical Output ⚡ (${data.pollAttempt || 1}/${data.maxAttempts || 60})`);
            } else if (data.phase === 'vertical_download') {
              setProcessingStep('🎬 Finalizing Vertical Output ✨');
            } else if (data.message) {
              setProcessingStep(data.message);
            } else {
              // Fallback if only percent is available for processing phase
              setProcessingStep(`Processing video: ${data.percent.toFixed(1)}%`);
            }
          }
          if (data.completed) {
            setProcessingProgress(100);
            if (data.s3Url && data.verticalUrl) {
              setProcessedVideoUrl(data.s3Url);
              setVerticalVideoUrl(data.verticalUrl);
              setProcessingStep('Processing complete! Both videos ready.');
              enqueueSnackbar('Video processed successfully! Both horizontal and vertical videos are ready.', { variant: 'success' });
              
              // Update Master Video List with both URLs
              updateMasterVideoListUrls(data.s3Url, data.verticalUrl);
            } else if (data.s3Url && data.retryVertical) {
              setProcessedVideoUrl(data.s3Url);
              setShowRetryVertical(true);
              setProcessingStep('Video processed. Vertical generation failed - click to retry.');
              enqueueSnackbar('Video processed successfully. Vertical video generation failed - would you like to try again?', { variant: 'warning' });
            } else if (data.s3Url && !data.verticalUrl) {
              setProcessedVideoUrl(data.s3Url);
              setProcessingStep('Video processed successfully.');
              enqueueSnackbar('Video processed and uploaded to S3', { variant: 'success' });
            } else if (data.verticalUrl && !data.s3Url) {
              setVerticalVideoUrl(data.verticalUrl);
              setProcessingStep('Vertical video generated. Main upload failed.');
              enqueueSnackbar('Vertical video generated successfully, but main upload failed.', { variant: 'warning' });
            } else if (data.errors) {
              setProcessingStep('Processing failed for both videos.');
              enqueueSnackbar(`Processing failed: ${data.errors.join(', ')}`, { variant: 'error' });
            } else {
              setProcessingStep('Processing complete!');
              enqueueSnackbar('Processing completed', { variant: 'info' });
            }
            processingSSE.close();
            if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
            setEventSource(prev => prev === processingSSE ? null : prev);
            setTimeout(() => {
              if (!showRetryVertical) { // Use state value here
                resetForm();
              }
            }, 3000);
          }
          // Handle old-style completion for backward compatibility (if necessary)
          if (data.status === "completed") {
            console.log('[SSE Debug] YouTube flow legacy completion format', data);
            setProcessingProgress(100);
            setProcessingStep('Processing complete!');
            
            // Check if this legacy completion has the S3 URLs
            if (data.s3Url && data.verticalUrl) {
              console.log('[SSE Debug] YouTube flow legacy completion has both URLs, updating sheet');
              setProcessedVideoUrl(data.s3Url);
              setVerticalVideoUrl(data.verticalUrl);
              enqueueSnackbar('Video processed successfully! Both horizontal and vertical videos are ready.', { variant: 'success' });
              
              // Update Master Video List with both URLs
              updateMasterVideoListUrls(data.s3Url, data.verticalUrl);
            } else {
              console.log('[SSE Debug] YouTube flow legacy completion missing URLs, starting polling:', data);
              enqueueSnackbar(`Video processed. Checking for completion details...`, { variant: 'info' });
              
              // Start polling for completion data
              if (data.videoId) {
                console.log('[SSE Debug] Starting completion polling for missing URLs');
                pollForCompletion(data.videoId);
              }
            }
            
            processingSSE.close();
            if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
            setEventSource(prev => prev === processingSSE ? null : prev);
            
            // Don't reset form immediately if we're polling for completion
            if (data.s3Url && data.verticalUrl) {
              setTimeout(() => {
                resetForm();
              }, 2000);
            }
          } else if (data.status === "failed") {
            console.error('SSE reported processing failure:', data.error);
            enqueueSnackbar(`Processing failed: ${data.error || 'Unknown FFmpeg error'}`, { variant: 'error' });
            setIsProcessing(false); // Ensure isProcessing is false on failure
            setProcessingStep('Processing failed');
            processingSSE.close();
            if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
            setEventSource(prev => prev === processingSSE ? null : prev);
          }
        } catch (parseError) {
          console.error('[SSE Debug] Failed to parse event data (Main Processing from YouTube flow):', parseError, 'Raw data:', event.data);
        }
      };

      processingSSE.onerror = (err) => {
        console.error('[SSE Debug] Main Processing EventSource failed (from YouTube flow):', err);
        if (processingSSE.readyState === EventSource.CLOSED) {
          console.log('[SSE Debug] Connection closed, starting polling for completion');
          // Instead of stopping processing, start polling for completion
          if (videoId) {
            pollForCompletion(videoId);
          }
          if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
          setEventSource(prev => prev === processingSSE ? null : prev);
        }
      };
    } catch (error) {
      console.error('YouTube download error:', error);
      enqueueSnackbar(`Download failed: ${error instanceof Error ? error.message : String(error)}`, { variant: 'error' });
      setIsDownloadingFromYoutube(false);
      setDownloadProgress(0);
      setDownloadStep('');
      
      if (downloadSSE && downloadSSE.readyState !== EventSource.CLOSED) {
        downloadSSE.close();
      }
       if (eventSourceRef.current === downloadSSE) eventSourceRef.current = null;
       setEventSource(prev => prev === downloadSSE ? null : prev);
    }
  }, [
    youtubeUrl, // For reading the input field value if urlToDownload param is not provided
    enqueueSnackbar, 
    resetForm, 
    videoRef, 
    eventSourceRef,
    eventSource, // For closing existing state.eventSource
    showRetryVertical, // Added based on usage within the function
    songFromVideoManager, // For passing metadata to backend
    artistFromVideoManager, // For passing metadata to backend
    pollForCompletion, // For starting polling when SSE fails
    updateMasterVideoListUrls // For updating sheet when SSE completes
    // All setters (setVideoId, setEventSource, etc.) are stable and don't need to be listed.
    // We are not depending on `videoId` (state) or `eventSource` (state) for re-creating the function,
    // as this function now manages its own `currentExecutionVideoId` and sets up its own EventSources.
    // It DOES read the `eventSource` state to close it, so it's needed.
  ]);

  // Effect to automatically start download when component mounts with YouTube URL
  useEffect(() => {
    // Only process once per component mount to avoid multiple state resets
    if (processedQueryParamsRef.current) {
      return;
    }
    
    const params = new URLSearchParams(location.search);
    const urlFromQuery = params.get('youtubeUrl');
    const rowIndex = params.get('videoManagerRowIndex');
    const song = params.get('song');
    const artist = params.get('artist');
    
    console.log('[UploadVideo] URL Parameters extracted:');
    console.log('  urlFromQuery:', urlFromQuery);
    console.log('  rowIndex:', rowIndex);
    console.log('  song:', song);
    console.log('  artist:', artist);
    
    // Mark as processed to prevent re-runs
    processedQueryParamsRef.current = true;
    
    if (urlFromQuery && !processedQueryUrlRef.current) {
      console.log(`[UploadVideo] Auto-starting download for URL: ${urlFromQuery}`);
      console.log(`[UploadVideo] Received metadata - Row: ${rowIndex}, Artist: ${artist}, Song: ${song}`);
      
      processedQueryUrlRef.current = urlFromQuery;
      setYoutubeUrl(urlFromQuery);
      
      if (rowIndex) {
        console.log('[UploadVideo] Setting videoManagerRowIndex to:', parseInt(rowIndex, 10));
        setVideoManagerRowIndex(parseInt(rowIndex, 10));
      }
      if (song) {
        console.log('[UploadVideo] Setting songFromVideoManager to:', song);
        setSongFromVideoManager(song);
      }
      if (artist) {
        console.log('[UploadVideo] Setting artistFromVideoManager to:', artist);
        setArtistFromVideoManager(artist);
      }
      
      downloadFromYoutube(urlFromQuery);
    } else if (!urlFromQuery) {
      console.log('[UploadVideo] No youtubeUrl in params, not auto-downloading');
    } else if (processedQueryUrlRef.current) {
      console.log('[UploadVideo] Already processed URL:', processedQueryUrlRef.current, '- not downloading again');
      
      // Still set the metadata if we haven't already
      if (rowIndex && videoManagerRowIndex === null) {
        console.log('[UploadVideo] Re-setting videoManagerRowIndex to:', parseInt(rowIndex, 10));
        setVideoManagerRowIndex(parseInt(rowIndex, 10));
      }
      if (song && !songFromVideoManager) {
        console.log('[UploadVideo] Re-setting songFromVideoManager to:', song);
        setSongFromVideoManager(song);
      }
      if (artist && !artistFromVideoManager) {
        console.log('[UploadVideo] Re-setting artistFromVideoManager to:', artist);
        setArtistFromVideoManager(artist);
      }
    }
  }, [location.search]); // Only run when location.search changes, not on every state change

  // Generate blurred background preview for 4:3 videos
  // Moved up and wrapped in useCallback
  const generateBlurredPreview = useCallback(async (metadata?: VideoMetadata): Promise<string | null> => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const metadataToUse = metadata || videoMetadata; // Use passed metadata or state metadata
    
    if (!video || !canvas || !metadataToUse) {
      console.error('[Blurred Preview] Missing dependencies:', { 
        hasVideo: !!video, 
        hasCanvas: !!canvas, 
        hasMetadata: !!metadataToUse 
      });
      return null;
    }

    try {
      console.log('[Blurred Preview] Starting preview generation...');
      console.log('[Blurred Preview] Using metadata:', metadataToUse);
      console.log('[Blurred Preview] Video state:', {
        readyState: video.readyState,
        currentTime: video.currentTime,
        duration: video.duration,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight
      });
      
      // Ensure video is at a good frame (seek to 25% of duration)
      const targetTime = metadataToUse.duration * 0.25;
      console.log('[Blurred Preview] Seeking to time:', targetTime);
      video.currentTime = targetTime;
      
      // Wait for video to seek to the target time
      await new Promise<void>((resolve) => {
        const handleSeeked = () => {
          console.log('[Blurred Preview] Seeked event fired');
          video.removeEventListener('seeked', handleSeeked);
          resolve();
        };
        video.addEventListener('seeked', handleSeeked);
        
        // Fallback timeout in case seeked doesn't fire
        setTimeout(() => {
          console.log('[Blurred Preview] Seek timeout - proceeding anyway');
          video.removeEventListener('seeked', handleSeeked);
          resolve();
        }, 1000);
      });

      console.log('[Blurred Preview] Video seeked to time:', video.currentTime);
      console.log('[Blurred Preview] Video ready state after seek:', video.readyState);

      // Set canvas to 16:9 aspect ratio (1920x1080)
      const targetWidth = 1920;
      const targetHeight = 1080;
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      console.log('[Blurred Preview] Canvas size set to:', targetWidth, 'x', targetHeight);
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('[Blurred Preview] Could not get canvas context');
        return null;
      }

      // Scale video to fit height, center horizontally
      const scale = targetHeight / metadataToUse.height;
      const scaledWidth = metadataToUse.width * scale;
      const scaledHeight = targetHeight;
      const offsetX = (targetWidth - scaledWidth) / 2;

      console.log('[Blurred Preview] Drawing calculations:', {
        scale,
        scaledWidth,
        scaledHeight,
        offsetX
      });

      console.log('[Blurred Preview] Drawing blurred background...');
      // First, draw the blurred background (stretched to fill entire canvas)
      ctx.filter = 'blur(20px)';
      try {
        ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
        console.log('[Blurred Preview] Blurred background drawn successfully');
      } catch (drawError) {
        console.error('[Blurred Preview] Error drawing blurred background:', drawError);
        return null;
      }
      
      console.log('[Blurred Preview] Drawing centered original video...');
      // Reset filter and draw the original video centered
      ctx.filter = 'none';
      try {
        ctx.drawImage(video, offsetX, 0, scaledWidth, scaledHeight);
        console.log('[Blurred Preview] Centered video drawn successfully');
      } catch (drawError) {
        console.error('[Blurred Preview] Error drawing centered video:', drawError);
        return null;
      }

      console.log('[Blurred Preview] Converting canvas to blob...');
      // Convert to blob URL
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (blob && blob.size > 0) {
            const url = URL.createObjectURL(blob);
            console.log('[Blurred Preview] Successfully created blob URL:', url, 'Size:', blob.size, 'bytes');
            resolve(url);
          } else {
            console.error('[Blurred Preview] Failed to create blob or blob is empty. Blob:', blob);
            resolve(null);
          }
        }, 'image/jpeg', 0.9);
      });
    } catch (error) {
      console.error('[Blurred Preview] Error generating preview:', error);
      return null;
    }
  }, [videoMetadata, videoRef, canvasRef]); // Added dependencies for useCallback

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      setUploadError('Please select a valid video file');
      return;
    }

    // Validate file size (limit to 500MB)
    if (file.size > 500 * 1024 * 1024) {
      setUploadError('File size must be less than 500MB');
      return;
    }

    setSelectedFile(file);
    setUploadError(null);
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    setShowCropOverlay(false);

    // Reset YouTube specific states if a local file is selected
    setYoutubeServerFilePath(null);
    setYoutubeOriginalFileName(null);
    setIsVideoFromYoutubeSource(false);

    // Generate a new UUID for this upload
    const newVideoId = uuidv4();
    setVideoId(newVideoId);

    // Close any previous EventSource
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }

    // Open SSE connection immediately - connect directly to backend to bypass proxy issues
    const sseUrl = process.env.NODE_ENV === 'development' 
              ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/video-progress/${encodeURIComponent(newVideoId)}`
      : `/api/video-progress/${encodeURIComponent(newVideoId)}`;
    
    const source = new EventSource(sseUrl);
    setProcessingProgress(0);
    setProcessingStep('Waiting for processing to start...');

    console.log('[SSE Debug] Opening connection to:', sseUrl);
    console.log('[SSE Debug] EventSource readyState:', source.readyState);

    // Add timeout to detect stuck connections
    let connectionTimeout: NodeJS.Timeout | null = null;
    connectionTimeout = setTimeout(() => {
      if (source.readyState === EventSource.CONNECTING) {
        console.error('[SSE Debug] Connection timeout - still in CONNECTING state after 10 seconds');
        source.close();
        enqueueSnackbar('Connection timeout. Please try again.', { variant: 'error' });
        setIsProcessing(false);
        setProcessingStep('Connection failed');
      }
    }, 10000); // 10 second timeout

    source.onopen = (event) => {
      if (connectionTimeout) clearTimeout(connectionTimeout); // Clear timeout on successful connection
      console.log('[SSE Debug] Connection opened successfully:', event);
      console.log('[SSE Debug] ReadyState after open:', source.readyState);
      console.log('[SSE Debug] URL:', source.url);
      
      // Debug: Log connection state every 2 seconds
      const debugInterval = setInterval(() => {
        console.log('[SSE Debug] Connection state check - ReadyState:', source.readyState, 'URL:', source.url);
      }, 2000);
      
      // Clean up interval when connection closes
      source.addEventListener('close', () => {
        clearInterval(debugInterval);
      });
    };

    source.onmessage = (event) => {
      console.log('[SSE Debug] Event received:', event.data);
      console.log('[SSE Debug] Event type:', event.type);
      try {
        const data = JSON.parse(event.data);
        console.log('[SSE Debug] Parsed data:', data);
        
        // Handle progress updates
        if (data.percent !== undefined) {
          const newProgress = Math.round(30 + (data.percent * 0.7));
          console.log('[SSE Debug] Setting progress to:', newProgress);
          setProcessingProgress(newProgress);
          
          // Handle different phases of processing
          if (data.phase === 'vertical_generation') {
            setProcessingStep('Generating vertical video...');
          } else if (data.phase === 'vertical_processing') {
            setProcessingStep(`Generating vertical video (Job: ${data.sieveJobId || 'processing'})...`);
          } else if (data.phase === 'vertical_polling') {
            setProcessingStep(`Processing vertical video (${data.pollAttempt || 1}/${data.maxAttempts || 60})...`);
          } else if (data.message) {
            setProcessingStep(data.message);
          } else {
            setProcessingStep(`Processing: ${data.percent.toFixed(1)}%`);
          }
        }
        
        // Handle completion
        if (data.completed) {
          console.log('[SSE Debug] Processing completed');
          setProcessingProgress(100);
          
          if (data.s3Url && data.verticalUrl) {
            // Both processes succeeded
            setProcessedVideoUrl(data.s3Url);
            setVerticalVideoUrl(data.verticalUrl);
            setProcessingStep('Processing complete! Both videos ready.');
            enqueueSnackbar('Video processed successfully! Both horizontal and vertical videos are ready.', { variant: 'success' });
            
            // Update Master Video List with both URLs
            updateMasterVideoListUrls(data.s3Url, data.verticalUrl);
          } else if (data.s3Url && data.retryVertical) {
            // Main video succeeded, vertical failed
            setProcessedVideoUrl(data.s3Url);
            setShowRetryVertical(true);
            setProcessingStep('Video processed. Vertical generation failed - click to retry.');
            enqueueSnackbar('Video processed successfully. Vertical video generation failed - would you like to try again?', { variant: 'warning' });
          } else if (data.s3Url && !data.verticalUrl) {
            // Only main video succeeded
            setProcessedVideoUrl(data.s3Url);
            setProcessingStep('Video processed successfully.');
            enqueueSnackbar('Video processed and uploaded to S3', { variant: 'success' });
          } else if (data.verticalUrl && !data.s3Url) {
            // Only vertical succeeded (unusual case)
            setVerticalVideoUrl(data.verticalUrl);
            setProcessingStep('Vertical video generated. Main upload failed.');
            enqueueSnackbar('Vertical video generated successfully, but main upload failed.', { variant: 'warning' });
          } else if (data.errors) {
            // Both failed
            setProcessingStep('Processing failed for both videos.');
            enqueueSnackbar(`Processing failed: ${data.errors.join(', ')}`, { variant: 'error' });
          } else {
            // Fallback for unclear completion state
            setProcessingStep('Processing complete!');
            enqueueSnackbar('Processing completed', { variant: 'info' });
          }
          
          source.close();
          setTimeout(() => {
            if (!showRetryVertical) {
              resetForm();
            }
          }, 3000);
        }
        
        // Handle old-style completion for backward compatibility
        if (data.status === "completed") {
          console.log('[SSE Debug] Legacy completion format', data);
          setProcessingProgress(100);
          setProcessingStep('Processing complete!');
          
          // Check if this legacy completion has the S3 URLs
          if (data.s3Url && data.verticalUrl) {
            console.log('[SSE Debug] Legacy completion has both URLs, updating sheet');
            setProcessedVideoUrl(data.s3Url);
            setVerticalVideoUrl(data.verticalUrl);
            enqueueSnackbar('Video processed successfully! Both horizontal and vertical videos are ready.', { variant: 'success' });
            
            // Update Master Video List with both URLs
            updateMasterVideoListUrls(data.s3Url, data.verticalUrl);
          } else {
            console.log('[SSE Debug] Legacy completion missing URLs, starting polling:', data);
            enqueueSnackbar(`Video processed. Checking for completion details...`, { variant: 'info' });
            
            // Start polling for completion data
            if (data.videoId) {
              console.log('[SSE Debug] Starting completion polling for missing URLs');
              pollForCompletion(data.videoId);
            }
          }
          
          source.close();
          
          // Don't reset form immediately if we're polling for completion
          if (data.s3Url && data.verticalUrl) {
            setTimeout(() => {
              resetForm();
            }, 2000);
          }
        } else if (data.status === "failed") {
          console.error('SSE reported processing failure:', data.error);
          enqueueSnackbar(`Processing failed: ${data.error || 'Unknown FFmpeg error'}`, { variant: 'error' });
          setIsProcessing(false);
          setProcessingStep('Processing failed');
          source.close();
        }
      } catch (parseError) {
        console.error('[SSE Debug] Failed to parse event data:', parseError, 'Raw data:', event.data);
      }
    };

          source.onerror = (err) => {
        if (connectionTimeout) clearTimeout(connectionTimeout); // Clear timeout on error
        console.error('[SSE Debug] EventSource failed:', err);
        console.error('[SSE Debug] ReadyState on error:', source.readyState);
        console.error('[SSE Debug] Error event details:', {
          type: err.type,
          target: err.target,
          currentTarget: err.currentTarget
        });
        enqueueSnackbar('Connection lost. Checking for completion...', { variant: 'warning' });
        
        // Start polling for completion as fallback
        if (videoId) {
          console.log('[SSE Debug] Starting completion polling due to SSE error');
          pollForCompletion(videoId);
        }
        
        // Don't immediately close on error - let it try to reconnect
        if (source.readyState === EventSource.CLOSED) {
          console.log('[SSE Debug] Connection closed due to error, processing state:', { isProcessing, processingProgress });
          setProcessingStep('Connection lost, checking completion...');
        }
      };

      // Add event listener for when the SSE connection closes
      source.addEventListener('close', () => {
        console.log('[SSE Debug] SSE connection explicitly closed');
      });
  };

  const handleVideoLoaded = useCallback(async () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const metadata: VideoMetadata = {
      width: video.videoWidth,
      height: video.videoHeight,
      duration: video.duration,
      aspectRatio: video.videoWidth / video.videoHeight
    };

    setVideoMetadata(metadata);
    setIsVideoLoaded(true);

    // Initialize crop area to full video
    setCropArea({
      x: 0,
      y: 0,
      width: video.videoWidth,
      height: video.videoHeight
    });

    console.log('Video loaded:', metadata);

    // Check if video is close to 4:3 aspect ratio (within 10% threshold)
    const targetAspectRatio = 4 / 3; // 1.333...
    const aspectDifference = Math.abs(metadata.aspectRatio - targetAspectRatio) / targetAspectRatio;
    const is43AspectRatio = aspectDifference < 0.1; // Within 10%

    if (is43AspectRatio) {
      console.log('[Auto 4:3 Detection] 4:3 video detected automatically:', {
        aspectRatio: metadata.aspectRatio,
        target: targetAspectRatio,
        difference: (aspectDifference * 100).toFixed(1) + '%'
      });
      
      // Set 4:3 state
      setIs43Video(true);
      
      // Wait a bit for video to be fully ready, then generate preview
      setTimeout(async () => {
        const previewUrl = await generateBlurredPreview(metadata);
        if (previewUrl) {
          setBlurredPreviewUrl(previewUrl);
          setShowCropOverlay(true); // Automatically show the preview
          console.log('[Auto 4:3 Detection] Automatically generated and showing blurred preview');
        } else {
          console.error('[Auto 4:3 Detection] Failed to generate preview automatically');
        }
      }, 500); // Small delay to ensure video is ready
    } else {
      console.log('[Auto 4:3 Detection] Not a 4:3 video:', {
        aspectRatio: metadata.aspectRatio,
        target: targetAspectRatio,
        difference: (aspectDifference * 100).toFixed(1) + '%'
      });
      
      // Reset 4:3 state for non-4:3 videos
      setIs43Video(false);
      setIsPillarboxed43(false);
      setIsLetterboxed(false);
      setIsPillarboxed(false);
      setBlurredPreviewUrl(null);
    }
  }, [generateBlurredPreview, videoMetadata]); // Added generateBlurredPreview to dependency array

  const calculateAspectRatioCrop = (newCrop: Partial<CropArea>, metadata: VideoMetadata): CropArea => {
    const targetAspectRatio = 16 / 9;
    let { x = cropArea.x, y = cropArea.y, width = cropArea.width, height = cropArea.height } = newCrop;

    // Ensure crop stays within video bounds
    x = Math.max(0, Math.min(x, metadata.width));
    y = Math.max(0, Math.min(y, metadata.height));

    // Adjust width/height to maintain 16:9 aspect ratio
    if (width && height) {
      const currentRatio = width / height;
      if (currentRatio > targetAspectRatio) {
        // Too wide, adjust height
        height = width / targetAspectRatio;
      } else {
        // Too tall, adjust width
        width = height * targetAspectRatio;
      }
    }

    // Ensure crop doesn't exceed video bounds
    if (x + width > metadata.width) {
      width = metadata.width - x;
      height = width / targetAspectRatio;
    }
    if (y + height > metadata.height) {
      height = metadata.height - y;
      width = height * targetAspectRatio;
    }

    return { x, y, width, height };
  };

  const detectOptimalCrop = async () => {
    // Guard clause: Ensure necessary data is available
    if (!videoRef.current || !videoMetadata || (!selectedFile && !youtubeServerFilePath)) {
      enqueueSnackbar('Video data not ready for crop detection.', { variant: 'warning' });
      return;
    }

    setIsAIDetecting(true);
    try {
      console.log(`[AI Crop Debug] Starting crop detection. Source: ${isVideoFromYoutubeSource ? 'YouTube (server file)' : 'Local file'}`);
      console.log('[AI Crop Debug] Video metadata:', videoMetadata);
      
      const formData = new FormData();
      formData.append('videoWidth', videoMetadata.width.toString());
      formData.append('videoHeight', videoMetadata.height.toString());

      if (isVideoFromYoutubeSource && youtubeServerFilePath) {
        console.log('[AI Crop Debug] Using YouTube server file path:', youtubeServerFilePath);
        formData.append('videoPath', youtubeServerFilePath); // Send server-relative path
        // Optionally, send original filename if backend needs it for logging/naming, though not strictly needed for crop detection
        if (youtubeOriginalFileName) {
          formData.append('originalFileName', youtubeOriginalFileName);
        }
      } else if (selectedFile) {
        console.log('[AI Crop Debug] Using local file:', selectedFile.name);
        formData.append('video', selectedFile); // Send the actual video file
      } else {
        // This case should ideally be caught by the guard clause at the top
        throw new Error('No valid video source (local file or YouTube path) for crop detection.');
      }

      try {
        console.log('[AI Crop Debug] Sending data to /api/detect-crop-area');
        const response = await fetch('/api/detect-crop-area', {
          method: 'POST',
          body: formData
        });

        console.log('[AI Crop Debug] Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[AI Crop Debug] Error response:', errorText);
          throw new Error(`AI crop detection failed: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        console.log('[AI Crop Debug] Success response:', result);
        
        if (result.success && result.cropArea) {
          // Check if this is a 4:3 detection (either full frame or pillarboxed content)
          const isFullFrame = result.cropArea.x === 0 && result.cropArea.y === 0 && 
                             result.cropArea.width === videoMetadata.width && 
                             result.cropArea.height === videoMetadata.height;
          
          const hasSpecialContent = result.message?.includes('Letterboxed') || result.message?.includes('Pillarboxed');
          const isLetterboxed = result.isLetterboxed || result.message?.includes('Letterboxed');
          const isPillarboxed = result.isPillarboxed || result.message?.includes('Pillarboxed');
          const isWideFile = result.isWideFile || result.message?.includes('Wide');
          
          if (isFullFrame) {
            // File-level 4:3 video - show blurred preview
            console.log('[AI Crop Debug] File-level 4:3 content detected - using full frame for blurred background processing');
            setCropArea(result.cropArea);
            setIs43Video(true);
            setIsPillarboxed43(false);
            setIsLetterboxed(false);
            setIsPillarboxed(false);
            setIsWideFile(false);
            
            // Generate blurred background preview
            const previewUrl = await generateBlurredPreview(videoMetadata);
            if (previewUrl) {
              setBlurredPreviewUrl(previewUrl);
              console.log('[Blurred Preview] Generated blurred background preview for file-level 4:3');
            }
          } else if (isWideFile) {
            // Wide video file (wider than 16:9) - show red crop overlay for 16:9 crop
            console.log('[AI Crop Debug] Wide video file detected - showing centered 16:9 crop area');
            setCropArea(result.cropArea);
            setIs43Video(false);
            setIsPillarboxed43(false);
            setIsLetterboxed(false);
            setIsPillarboxed(false);
            setIsWideFile(true);
            setBlurredPreviewUrl(null);
          } else if (hasSpecialContent) {
            // Letterboxed or Pillarboxed content - show crop overlay
            console.log('[AI Crop Debug] Special content detected:', {
              type: result.contentType,
              isLetterboxed,
              isPillarboxed,
              message: result.message
            });
            setCropArea(result.cropArea); // Use exact coordinates from server
            setIs43Video(false);
            setIsPillarboxed43(isPillarboxed && !isLetterboxed); // Only pillarboxed gets blurred background, NOT letterboxed
            setIsLetterboxed(isLetterboxed);
            setIsPillarboxed(isPillarboxed);
            setBlurredPreviewUrl(null);
          } else {
            // Regular 16:9 content - apply aspect ratio adjustment
            console.log('[AI Crop Debug] Regular 16:9 content detected - applying aspect ratio adjustment');
            const aiCrop = calculateAspectRatioCrop(result.cropArea, videoMetadata);
            setCropArea(aiCrop);
            setIs43Video(false);
            setIsPillarboxed43(false);
            setIsLetterboxed(false);
            setIsPillarboxed(false);
            setBlurredPreviewUrl(null);
          }
          
          setShowCropOverlay(true);
        } else {
          throw new Error(result.message || 'Failed to detect crop area');
        }
      } catch (error) {
        console.error('[AI Crop Debug] Network/API error:', error);
        enqueueSnackbar(`AI crop detection failed: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'warning' });
        
        // Fallback: suggest center crop
        const centerCrop = {
          x: videoMetadata.width * 0.1,
          y: videoMetadata.height * 0.1,
          width: videoMetadata.width * 0.8,
          height: videoMetadata.height * 0.8
        };
        const fallbackCrop = calculateAspectRatioCrop(centerCrop, videoMetadata);
        setCropArea(fallbackCrop);
        setIs43Video(false);
        setIsPillarboxed43(false);
        setIsLetterboxed(false);
        setIsPillarboxed(false);
        setBlurredPreviewUrl(null);
        setShowCropOverlay(true);
      }

    } catch (error) {
      console.error('[AI Crop Debug] General error:', error);
      enqueueSnackbar(`Failed to analyze video: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
    } finally {
      setIsAIDetecting(false);
    }
  };

  const handleCropChange = (field: keyof CropArea, value: number) => {
    if (!videoMetadata) return;

    const newCrop = { ...cropArea, [field]: value };
    const adjustedCrop = calculateAspectRatioCrop(newCrop, videoMetadata);
    setCropArea(adjustedCrop);
  };

  const processVideo = async () => {
    // Guard clause: Ensure necessary data for processing is available
    if (!videoMetadata || !videoId || (!selectedFile && !youtubeServerFilePath)) {
      enqueueSnackbar('No valid video source selected for processing', { variant: 'error' });
      return;
    }

    setIsProcessing(true);
    setProcessingProgress(0); 
    setProcessingStep(isVideoFromYoutubeSource ? 'Preparing downloaded video...' : 'Uploading video...');

    try {
      const formData = new FormData();
      formData.append('cropArea', JSON.stringify(cropArea));
      formData.append('targetWidth', '1920');
      formData.append('targetHeight', '1080');
      formData.append('videoId', videoId);

      // Add song/artist metadata if available (from YouTube download)
      if (songFromVideoManager) {
        formData.append('song', songFromVideoManager);
        console.log('[Process Video] Including song metadata:', songFromVideoManager);
      }
      if (artistFromVideoManager) {
        formData.append('artist', artistFromVideoManager);
        console.log('[Process Video] Including artist metadata:', artistFromVideoManager);
      }

      if (isVideoFromYoutubeSource && youtubeServerFilePath) {
        console.log('[Process Video] Using YouTube server file path:', youtubeServerFilePath);
        formData.append('videoPath', youtubeServerFilePath);
        if (youtubeOriginalFileName) {
          formData.append('originalFileName', youtubeOriginalFileName);
        }
      } else if (selectedFile) {
        console.log('[Process Video] Using local file:', selectedFile.name);
        formData.append('video', selectedFile);
      } else {
        throw new Error('No valid video source (local file or YouTube path) for processing.');
      }
      
      // If it's a YouTube video, we skip the XHR upload progress part as the file is already on the server.
      // We will directly initiate the processing via a fetch call that the backend SSE will then track.
      if (isVideoFromYoutubeSource) {
        setProcessingStep('Initiating processing for downloaded video...');
        const processResponse = await fetch('/api/process-video', {
          method: 'POST',
          body: formData,
        });

        if (!processResponse.ok) {
          const errorBody = await processResponse.text();
          throw new Error(`Processing initialization failed: ${processResponse.status} - ${errorBody}`);
        }

        const result = await processResponse.json();
        if (result.success) {
          setProcessingStep('Processing video (see progress below)...');
          // SSE connection should already be open from handleFileSelect or downloadFromYoutube
          // and will receive detailed progress updates from the backend.
        } else {
          throw new Error(result.message || 'Processing initialization failed on server');
        }

      } else {
        // For local files, use XMLHttpRequest for upload progress as before
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const uploadPercent = Math.round((event.loaded / event.total) * 30); 
            setProcessingProgress(uploadPercent);
            if (uploadPercent === 30) {
              setProcessingStep('Upload complete. Starting processing...');
            } else {
              setProcessingStep(`Uploading: ${uploadPercent}%`);
            }
          }
        };

        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const processResponseData = JSON.parse(xhr.responseText);
              if (processResponseData.success) {
                setProcessingStep('Processing video (see progress below)...');
                // SSE connection is already open and will handle further progress
              } else {
                throw new Error(processResponseData.message || 'Processing initialization failed after upload');
              }
            } catch (e) {
              console.error('Error parsing process response or setting up SSE:', e);
              enqueueSnackbar(`Error during processing setup: ${e instanceof Error ? e.message : 'Unknown error'}`, { variant: 'error' });
              setIsProcessing(false);
              setProcessingStep('Error setting up processing');
            }
          } else {
            let errorMsg = `Processing request failed: ${xhr.status}`;
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              if (errorResponse && errorResponse.details) {
                errorMsg += ` - ${errorResponse.details}`;
              } else if (errorResponse && errorResponse.error) {
                errorMsg += ` - ${errorResponse.error}`;
              }
            } catch (e) { /* ignore if response is not json */ }
            enqueueSnackbar(errorMsg, { variant: 'error' });
            setIsProcessing(false);
            setProcessingStep('Processing request error');
          }
        };

        xhr.onerror = () => {
          enqueueSnackbar('Network error during video processing request.', { variant: 'error' });
          setIsProcessing(false);
          setProcessingStep('Network error');
        };

        xhr.open('POST', '/api/process-video', true);
        xhr.send(formData);
      }

    } catch (error) {
      console.error('Video processing error (outer try-catch):', error);
      enqueueSnackbar(`Processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
      setIsProcessing(false);
      setProcessingProgress(0);
      setProcessingStep('Critical error');
    }
  };

  // Cleanup video URL on unmount
  const currentVideoUrl = videoUrl;
  const currentBlurredPreviewUrl = blurredPreviewUrl;
  useEffect(() => {
    return () => {
      if (currentVideoUrl) {
        URL.revokeObjectURL(currentVideoUrl);
      }
      if (currentBlurredPreviewUrl) {
        URL.revokeObjectURL(currentBlurredPreviewUrl);
      }
    };
  }, [currentVideoUrl, currentBlurredPreviewUrl]); // Corrected dependencies

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        console.log('[SSE Debug] Cleaning up EventSource on unmount');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  // Effect to update playerRect when video loads or window resizes
  useEffect(() => {
    const updatePlayerRect = () => {
      if (videoRef.current && isVideoLoaded) {
        const rect = videoRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setPlayerRect({ width: rect.width, height: rect.height });
        }
      }
    };

    updatePlayerRect(); // Initial call

    window.addEventListener('resize', updatePlayerRect);
    return () => {
      window.removeEventListener('resize', updatePlayerRect);
    };
  }, [isVideoLoaded, videoUrl]); // videoUrl ensures re-calc if video source changes

  const cropOverlayStyleProps = useMemo(() => {
    if (!videoMetadata || !cropArea || !playerRect || playerRect.width === 0 || playerRect.height === 0) {
      return { top: '0%', left: '0%', width: '0%', height: '0%' };
    }

    const playerWidth = playerRect.width;
    const playerHeight = playerRect.height;

    const videoNaturalWidth = videoMetadata.width;
    const videoNaturalHeight = videoMetadata.height;

    const playerContainerAspectRatio = playerWidth / playerHeight;
    const videoContentAspectRatio = videoNaturalWidth / videoNaturalHeight;

    let actualDisplayedContentWidth, actualDisplayedContentHeight, contentOffsetX_px, contentOffsetY_px;

    if (videoContentAspectRatio > playerContainerAspectRatio) {
      // Video content is wider than player container (letterboxed)
      actualDisplayedContentWidth = playerWidth;
      actualDisplayedContentHeight = playerWidth / videoContentAspectRatio;
      contentOffsetX_px = 0;
      contentOffsetY_px = (playerHeight - actualDisplayedContentHeight) / 2;
    } else {
      // Video content is narrower or same aspect as player container (pillarboxed or fills)
      actualDisplayedContentHeight = playerHeight;
      actualDisplayedContentWidth = playerHeight * videoContentAspectRatio;
      contentOffsetY_px = 0;
      contentOffsetX_px = (playerWidth - actualDisplayedContentWidth) / 2;
    }

    // Ensure no division by zero if natural width/height are zero (though unlikely with videoMetadata check)
    if (videoNaturalWidth === 0 || videoNaturalHeight === 0) {
        return { top: '0%', left: '0%', width: '0%', height: '0%' };
    }
    
    const scale = actualDisplayedContentWidth / videoNaturalWidth;

    const cropOnScreenX_px = contentOffsetX_px + (cropArea.x * scale);
    const cropOnScreenY_px = contentOffsetY_px + (cropArea.y * scale);
    const cropOnScreenWidth_px = cropArea.width * scale;
    const cropOnScreenHeight_px = cropArea.height * scale;

    return {
      top: `${(cropOnScreenY_px / playerHeight) * 100}%`,
      left: `${(cropOnScreenX_px / playerWidth) * 100}%`,
      width: `${(cropOnScreenWidth_px / playerWidth) * 100}%`,
      height: `${(cropOnScreenHeight_px / playerHeight) * 100}%`,
    };
  }, [videoMetadata, cropArea, playerRect]);

  // Add mouse event handlers for corner dragging
  const handleCornerMouseDown = (corner: 'tl' | 'tr' | 'bl' | 'br', event: React.MouseEvent) => {
    if (!videoRef.current || !videoMetadata) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const videoRect = videoRef.current.getBoundingClientRect();
    const startX = event.clientX - videoRect.left;
    const startY = event.clientY - videoRect.top;
    
    setDragCorner(corner);
    setDragStart({
      x: startX,
      y: startY,
      cropArea: { ...cropArea }
    });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!dragStart || !videoRef.current || !videoMetadata || !dragCorner) return;
    
    const videoRect = videoRef.current.getBoundingClientRect();
    const currentX = event.clientX - videoRect.left;
    const currentY = event.clientY - videoRect.top;
    
    // Convert screen coordinates to video coordinates
    const scaleX = videoMetadata.width / videoRect.width;
    const scaleY = videoMetadata.height / videoRect.height;
    
    const deltaX = (currentX - dragStart.x) * scaleX;
    const deltaY = (currentY - dragStart.y) * scaleY;
    
    const originalCrop = dragStart.cropArea;
    let newCrop = { ...originalCrop };
    
    // Update crop area based on which corner is being dragged
    switch (dragCorner) {
      case 'tl': // Top-left corner
        newCrop.x = originalCrop.x + deltaX;
        newCrop.y = originalCrop.y + deltaY;
        newCrop.width = originalCrop.width - deltaX;
        newCrop.height = originalCrop.height - deltaY;
        break;
      case 'tr': // Top-right corner
        newCrop.y = originalCrop.y + deltaY;
        newCrop.width = originalCrop.width + deltaX;
        newCrop.height = originalCrop.height - deltaY;
        break;
      case 'bl': // Bottom-left corner
        newCrop.x = originalCrop.x + deltaX;
        newCrop.width = originalCrop.width - deltaX;
        newCrop.height = originalCrop.height + deltaY;
        break;
      case 'br': // Bottom-right corner
        newCrop.width = originalCrop.width + deltaX;
        newCrop.height = originalCrop.height + deltaY;
        break;
    }
    
    // Ensure crop area stays within video bounds and has minimum size
    newCrop.x = Math.max(0, newCrop.x);
    newCrop.y = Math.max(0, newCrop.y);
    newCrop.width = Math.max(50, Math.min(newCrop.width, videoMetadata.width - newCrop.x));
    newCrop.height = Math.max(50, Math.min(newCrop.height, videoMetadata.height - newCrop.y));
    
    setCropArea(newCrop);
  };

  const handleMouseUp = () => {
    setDragCorner(null);
    setDragStart(null);
  };

  // Add useEffect to handle global mouse events during dragging
  useEffect(() => {
    if (!dragStart) return;
    
    const handleGlobalMouseMove = (event: MouseEvent) => {
      if (!dragStart || !videoRef.current || !videoMetadata || !dragCorner) return;
      
      const videoRect = videoRef.current.getBoundingClientRect();
      const currentX = event.clientX - videoRect.left;
      const currentY = event.clientY - videoRect.top;
      
      // Convert screen coordinates to video coordinates
      const scaleX = videoMetadata.width / videoRect.width;
      const scaleY = videoMetadata.height / videoRect.height;
      
      const deltaX = (currentX - dragStart.x) * scaleX;
      const deltaY = (currentY - dragStart.y) * scaleY;
      
      const originalCrop = dragStart.cropArea;
      let newCrop = { ...originalCrop };
      
      // Update crop area based on which corner is being dragged
      switch (dragCorner) {
        case 'tl': // Top-left corner
          newCrop.x = originalCrop.x + deltaX;
          newCrop.y = originalCrop.y + deltaY;
          newCrop.width = originalCrop.width - deltaX;
          newCrop.height = originalCrop.height - deltaY;
          break;
        case 'tr': // Top-right corner
          newCrop.y = originalCrop.y + deltaY;
          newCrop.width = originalCrop.width + deltaX;
          newCrop.height = originalCrop.height - deltaY;
          break;
        case 'bl': // Bottom-left corner
          newCrop.x = originalCrop.x + deltaX;
          newCrop.width = originalCrop.width - deltaX;
          newCrop.height = originalCrop.height + deltaY;
          break;
        case 'br': // Bottom-right corner
          newCrop.width = originalCrop.width + deltaX;
          newCrop.height = originalCrop.height + deltaY;
          break;
      }
      
      // Ensure crop area stays within video bounds and has minimum size
      newCrop.x = Math.max(0, newCrop.x);
      newCrop.y = Math.max(0, newCrop.y);
      newCrop.width = Math.max(50, Math.min(newCrop.width, videoMetadata.width - newCrop.x));
      newCrop.height = Math.max(50, Math.min(newCrop.height, videoMetadata.height - newCrop.y));
      
      setCropArea(newCrop);
    };
    
    const handleGlobalMouseUp = () => {
      setDragCorner(null);
      setDragStart(null);
    };
    
    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [dragStart, dragCorner, videoMetadata]);

  // Add retry function for vertical video generation
  const retryVerticalGeneration = async () => {
    if (!processedVideoUrl || !selectedFile) {
      enqueueSnackbar('Cannot retry - original video or file not available', { variant: 'error' });
      return;
    }

    setShowRetryVertical(false);
    setIsProcessing(true);
    setProcessingProgress(85);
    setProcessingStep('Retrying vertical video generation...');

    try {
      // Call backend endpoint to retry vertical generation using the already processed S3 URL
      const response = await fetch('/api/retry-vertical-generation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          processedVideoUrl,
          originalFilename: selectedFile.name,
          videoId: videoId || 'retry-' + Date.now()
        }),
      });

      if (!response.ok) {
        throw new Error(`Retry failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.verticalUrl) {
        setVerticalVideoUrl(result.verticalUrl);
        setProcessingProgress(100);
        setProcessingStep('Vertical video generated successfully!');
        enqueueSnackbar('Vertical video generated successfully!', { variant: 'success' });
        
        setTimeout(() => {
          resetForm();
        }, 3000);
      } else {
        throw new Error(result.error || 'Retry failed with unknown error');
      }
    } catch (error) {
      console.error('Retry vertical generation error:', error);
      enqueueSnackbar(`Retry failed: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
      setShowRetryVertical(true); // Show retry option again
      setProcessingStep('Vertical generation retry failed - click to try again');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={{ 
      mt: 0,
      mx: '2.5%',
      width: '95vw',
      maxWidth: '95vw',
      boxSizing: 'border-box',
      overflow: 'hidden',
      position: 'relative',
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '0 10%'
    }}>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <Button
          variant="contained"
          onClick={() => navigate('/video-manager')}
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px'
          }}
        >
          Video Manager
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Upload Section */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VideoFileIcon /> Video Upload
            </Typography>
            
            {/* Refactored to a horizontal layout */}
            <Box sx={{ 
              mb: 3, 
              p: 3, 
              border: '2px dashed #ccc', 
              borderRadius: 2, 
              display: 'flex', // Ensures horizontal layout
              alignItems: 'center', // Aligns items vertically in the center
              gap: 2 // Adds space between elements
            }}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="video/*"
                style={{ display: 'none' }}
              />
              <Button
                variant="contained"
                color="primary" // Ensures consistent button styling
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing || isDownloadingFromYoutube}
                sx={{ height: '56px', flexShrink: 0 }} // Fixed height, prevents shrinking
              >
                Select Video File
              </Button>

              <Typography variant="body1" sx={{ mx: 1, fontWeight: 'bold' }}>
                OR
              </Typography>
              
              {/* YouTube Download Section - now part of the flex layout */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="YouTube URL"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  disabled={isProcessing || isDownloadingFromYoutube}
                  size="medium" // Standard size for TextField
                />
                <Button
                  variant="contained"
                  color="primary" // Matches the Select Video File button
                  onClick={() => downloadFromYoutube(youtubeUrl)}
                  disabled={isProcessing || isDownloadingFromYoutube || !youtubeUrl.trim()}
                  sx={{ height: '56px', flexShrink: 0 }} // Fixed height, prevents shrinking
                >
                  {isDownloadingFromYoutube ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Select Youtube Video' // Corrected button text
                  )}
                </Button>
              </Box>
            </Box>

            {/* Moved progress and selected file name below the input group */}
            {isDownloadingFromYoutube && (
              <Box sx={{ mt: 2, mb: 2 }}> {/* Adjusted margin for standalone display */}
                <LinearProgress 
                  variant="determinate" 
                  value={downloadProgress} 
                  sx={{ mb: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {downloadStep} ({Math.round(downloadProgress)}%)
                </Typography>
              </Box>
            )}

            {selectedFile && !isDownloadingFromYoutube && ( // Only show if not YT downloading
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Selected: {selectedFile.name}
              </Typography>
            )}

            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadError}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Video Preview and Crop Section */}
        {videoUrl && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CropIcon /> Video Preview & Crop
              </Typography>

              {/* Video Player */}
              <Box sx={{ position: 'relative', mb: 3 }}>
                {is43Video && showCropOverlay && blurredPreviewUrl ? (
                  // Show blurred background preview for 4:3 videos (replaces video player)
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '16 / 9', // Lock to 16:9 aspect ratio
                      position: 'relative',
                      backgroundColor: '#000',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      border: '3px solid #4caf50',
                      boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)'
                    }}
                  >
                    <Box
                      component="img"
                      src={blurredPreviewUrl}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover', // Fill the 16:9 container completely
                        objectPosition: 'center'
                      }}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        bottom: '5%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: '#4caf50',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                    >
                      4:3 Preview - Blurred Background Style
                    </Typography>
                  </Box>
                ) : (
                  // Show regular video player
                  <Box sx={{ /* New container for 16:9 aspect ratio */
                    position: 'relative',
                    width: '100%',
                    paddingTop: '56.25%', /* 16:9 Aspect Ratio */
                    backgroundColor: '#000', /* Background for letter/pillarboxing */
                    borderRadius: '4px', /* Optional: match other styling */
                    overflow: 'hidden' /* Optional: ensure children don't overflow */
                  }}>
                    <video
                      ref={videoRef}
                      src={videoUrl}
                      controls
                      crossOrigin="anonymous"
                      onLoadedMetadata={handleVideoLoaded}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      style={{ /* Video fills the 16:9 container */
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain', /* Fit video within container, letter/pillarboxing as needed */
                        backgroundColor: '#000', /* Fallback, though parent has it */
                      }}
                    />
                    
                    {/* Enhanced Crop Overlay with Draggable Corners */}
                    {showCropOverlay && videoMetadata && (isLetterboxed || isPillarboxed43 || isWideFile || (!is43Video && !isPillarboxed43)) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          pointerEvents: 'none',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: cropOverlayStyleProps.top,
                            left: cropOverlayStyleProps.left,
                            width: cropOverlayStyleProps.width,
                            height: cropOverlayStyleProps.height,
                            border: isLetterboxed ? '3px solid #ff4444' : isPillarboxed ? '3px solid #ff9800' : isPillarboxed43 ? '3px solid #4caf50' : isWideFile ? '3px solid #ff4444' : '2px solid #ff4444',
                            backgroundColor: isLetterboxed ? 'rgba(255, 68, 68, 0.1)' : isPillarboxed ? 'rgba(255, 152, 0, 0.1)' : isPillarboxed43 ? 'rgba(76, 175, 80, 0.1)' : isWideFile ? 'rgba(255, 68, 68, 0.1)' : 'rgba(255, 68, 68, 0.1)',
                            boxSizing: 'border-box'
                          }
                        }}
                      >
                        {/* Corner Handles */}
                        {(isLetterboxed || isPillarboxed43 || isWideFile || (!is43Video && !isPillarboxed43)) && (
                          <>
                            {/* Top-left corner */}
                            <Box
                              onMouseDown={(e) => handleCornerMouseDown('tl', e)}
                              sx={{
                                position: 'absolute',
                                top: cropOverlayStyleProps.top,
                                left: cropOverlayStyleProps.left,
                                width: '12px',
                                height: '12px',
                                backgroundColor: isLetterboxed ? '#ff4444' : isPillarboxed ? '#ff9800' : isPillarboxed43 ? '#4caf50' : isWideFile ? '#ff4444' : '#ff4444',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'nw-resize',
                                pointerEvents: 'auto',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 10,
                                '&:hover': {
                                  backgroundColor: isLetterboxed ? '#ff6666' : isPillarboxed ? '#ffb74d' : isPillarboxed43 ? '#66bb6a' : isWideFile ? '#f57c00' : '#f57c00',
                                  transform: 'translate(-50%, -50%) scale(1.2)'
                                }
                              }}
                            />
                            
                            {/* Top-right corner */}
                            <Box
                              onMouseDown={(e) => handleCornerMouseDown('tr', e)}
                              sx={{
                                position: 'absolute',
                                top: cropOverlayStyleProps.top, // Uses the top of the main crop box
                                left: `calc(${cropOverlayStyleProps.left} + ${cropOverlayStyleProps.width})`, // Uses left + width of main crop box
                                width: '12px',
                                height: '12px',
                                backgroundColor: isLetterboxed ? '#ff4444' : isPillarboxed ? '#ff9800' : isPillarboxed43 ? '#4caf50' : isWideFile ? '#ff4444' : '#ff4444',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'ne-resize',
                                pointerEvents: 'auto',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 10,
                                '&:hover': {
                                  backgroundColor: isLetterboxed ? '#ff6666' : isPillarboxed ? '#ffb74d' : isPillarboxed43 ? '#66bb6a' : isWideFile ? '#f57c00' : '#f57c00',
                                  transform: 'translate(-50%, -50%) scale(1.2)'
                                }
                              }}
                            />
                            
                            {/* Bottom-left corner */}
                            <Box
                              onMouseDown={(e) => handleCornerMouseDown('bl', e)}
                              sx={{
                                position: 'absolute',
                                top: `calc(${cropOverlayStyleProps.top} + ${cropOverlayStyleProps.height})`, // Uses top + height of main crop box
                                left: cropOverlayStyleProps.left, // Uses the left of the main crop box
                                width: '12px',
                                height: '12px',
                                backgroundColor: isLetterboxed ? '#ff4444' : isPillarboxed ? '#ff9800' : isPillarboxed43 ? '#4caf50' : isWideFile ? '#ff4444' : '#ff4444',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'sw-resize',
                                pointerEvents: 'auto',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 10,
                                '&:hover': {
                                  backgroundColor: isLetterboxed ? '#ff6666' : isPillarboxed ? '#ffb74d' : isPillarboxed43 ? '#66bb6a' : isWideFile ? '#f57c00' : '#f57c00',
                                  transform: 'translate(-50%, -50%) scale(1.2)'
                                }
                              }}
                            />
                            
                            {/* Bottom-right corner */}
                            <Box
                              onMouseDown={(e) => handleCornerMouseDown('br', e)}
                              sx={{
                                position: 'absolute',
                                top: `calc(${cropOverlayStyleProps.top} + ${cropOverlayStyleProps.height})`, // Uses top + height of main crop box
                                left: `calc(${cropOverlayStyleProps.left} + ${cropOverlayStyleProps.width})`, // Uses left + width of main crop box
                                width: '12px',
                                height: '12px',
                                backgroundColor: isLetterboxed ? '#ff4444' : isPillarboxed ? '#ff9800' : isPillarboxed43 ? '#4caf50' : isWideFile ? '#ff4444' : '#ff4444',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'se-resize',
                                pointerEvents: 'auto',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 10,
                                '&:hover': {
                                  backgroundColor: isLetterboxed ? '#ff6666' : isPillarboxed ? '#ffb74d' : isPillarboxed43 ? '#66bb6a' : isWideFile ? '#f57c00' : '#f57c00',
                                  transform: 'translate(-50%, -50%) scale(1.2)'
                                }
                              }}
                            />
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {/* Video Metadata */}
              {videoMetadata && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Video Information:</Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Chip label={`${videoMetadata.width}x${videoMetadata.height}`} size="small" />
                    <Chip label={`${videoMetadata.duration.toFixed(1)}s`} size="small" />
                    <Chip label={`${videoMetadata.aspectRatio.toFixed(2)}:1`} size="small" />
                  </Box>
                </Box>
              )}

              {/* AI Crop Detection */}
              {isVideoLoaded && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={isAIDetecting ? <CircularProgress size={20} /> : <SmartToyIcon />}
                    onClick={detectOptimalCrop}
                    disabled={isAIDetecting || isProcessing}
                    sx={{ mr: 2 }}
                  >
                    {isAIDetecting ? 'Analyzing...' : (is43Video ? 'Regenerate Preview' : 'AI Detect Crop')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setShowCropOverlay(!showCropOverlay)}
                    disabled={isProcessing}
                  >
                    {showCropOverlay ? (is43Video ? 'Hide Preview' : 'Hide Crop') : (is43Video ? 'Show Preview' : 'Show Crop')}
                  </Button>
                </Box>
              )}

              {/* Crop Controls or Preview Info */}
              {showCropOverlay && videoMetadata && (
                <Box sx={{ mt: 3 }}>
                  {is43Video ? (
                    // Show info for file-level 4:3 videos
                    <Box>
                      <Typography variant="subtitle2" gutterBottom sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                        4:3 Video Detected - Blurred Background Style:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your video will be processed with the original 4:3 content centered vertically and a beautifully blurred background filling the 16:9 frame. No cropping will be applied to preserve all your content.
                      </Typography>
                    </Box>
                  ) : !isPillarboxed43 && (
                    // Show crop controls for regular 16:9 videos
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Crop Settings (16:9 aspect ratio):</Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
                        <TextField
                          label="X Position"
                          type="number"
                          value={Math.round(cropArea.x)}
                          onChange={(e) => handleCropChange('x', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 0, max: videoMetadata.width }}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Y Position"
                          type="number"
                          value={Math.round(cropArea.y)}
                          onChange={(e) => handleCropChange('y', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 0, max: videoMetadata.height }}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Width"
                          type="number"
                          value={Math.round(cropArea.width)}
                          onChange={(e) => handleCropChange('width', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 1, max: videoMetadata.width }}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Height"
                          type="number"
                          value={Math.round(cropArea.height)}
                          onChange={(e) => handleCropChange('height', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 1, max: videoMetadata.height }}
                          size="small"
                          fullWidth
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Processing Section */}
        {isVideoLoaded && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Process Video
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {is43Video || isPillarboxed43
                  ? "This will process your video with blurred background styling, scale to 1920x1080, normalize audio to ATSC A/85 standards, and upload to S3."
                  : "This will crop the video to 16:9 aspect ratio, scale to 1920x1080, normalize audio to ATSC A/85 standards, and upload to S3."
                }
              </Typography>

              {!isProcessing ? (
                <Button
                  variant="contained"
                  size="large"
                  onClick={processVideo}
                  disabled={!showCropOverlay}
                  sx={{ mr: 2 }}
                >
                  Process & Upload Video
                </Button>
              ) : (
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {processingStep}
                    </Typography>
                    <Typography variant="body2">
                      {processingProgress}%
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={processingProgress} />
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Video Results Section */}
        {(processedVideoUrl || verticalVideoUrl) && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Video Results
              </Typography>
              
              {/* Processed Video Result */}
              {processedVideoUrl && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50' }} />
                    Processed Video Ready
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => window.open(processedVideoUrl, '_blank')}
                      sx={{ flexShrink: 0 }}
                    >
                      View Video
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {processedVideoUrl.substring(processedVideoUrl.lastIndexOf('/') + 1)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Vertical Video Result */}
              {verticalVideoUrl ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50' }} />
                    Vertical Video Ready
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => window.open(verticalVideoUrl, '_blank')}
                      sx={{ flexShrink: 0 }}
                    >
                      View Vertical Video
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {verticalVideoUrl.substring(verticalVideoUrl.lastIndexOf('/') + 1)}
                    </Typography>
                  </Box>
                </Box>
              ) : showRetryVertical && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Vertical video generation failed. Would you like to try again?
                  </Alert>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={retryVerticalGeneration}
                    disabled={isProcessing}
                  >
                    Retry Vertical Generation
                  </Button>
                </Box>
              )}

              {/* Reset Button */}
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={isProcessing}
                >
                  Process Another Video
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Hidden canvas for AI crop detection */}
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />
    </Box>
  );
}; 